import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import KeywordCell from "../../components/custom/category/advertisements/KeywordCell";
import { createColumnBuilder } from "../../components/table/columnBuilder";
import { useStandardEntityMetrics } from "../merchandising/components/tables/metricUtils";
import { css } from "../../../styled-system/css";
import { type SearchTermPerformanceV4Row } from "./api/v4SearchTermPerformanceReportByQuery";
import { getDashboardTo } from "../../views/AdvertisingOverview";
import TrellisLink from "../../components/core/basic/TrellisLink";
import { SEARCH_TERM_DIMENSIONS } from "./utils/reportingConstants";
import { MATCH_TYPE_SELECTORS } from "../advertising/filters/targetLifecycleFilters";

const columnHelper = createColumnBuilder<SearchTermPerformanceV4Row>();

const useSearchTermReportColumns = (dimensions: string) => {
  const standardMetricIds = useStandardEntityMetrics("searchTerm");
  const { t } = useTranslation(["str", "advertising"]);

  const columns = useMemo(() => {
    const cols = [];

    if (dimensions.includes(SEARCH_TERM_DIMENSIONS.QUERY)) {
      cols.push(
        columnHelper.accessor("query", {
          id: "query",
          header: t(($) => $.str.columns["Query"]),
          enableHiding: false,
          size: 300,
          cell: (props) => {
            return (
              <div className={css({ paddingLeft: "small" })}>
                <KeywordCell value={props.row.original.query} columnWidth={300} />
              </div>
            );
          },
        }),
      );
    }

    if (dimensions.includes(SEARCH_TERM_DIMENSIONS.KEYWORD_TEXT)) {
      cols.push(
        columnHelper.accessor("keyword_text", {
          id: "keyword_text",
          header: t(($) => $.str.columns["Keyword Text"]),
          enableHiding: false,
          size: 300,
          cell: (props) => {
            const { ad_id, keyword_text, ad_name } = props.row.original;
            const showAdName = [
              "campaign_id",
              SEARCH_TERM_DIMENSIONS.QUERY_MATCH_KEYWORD,
              SEARCH_TERM_DIMENSIONS.QUERY_MATCH_TARGET,
            ].includes(dimensions);
            const adName = showAdName ? ad_name : "";

            return (
              <div className={css({ paddingLeft: "small" })}>
                <KeywordCell value={keyword_text} columnWidth={300} />
                {showAdName && adName && (
                  <>
                    <br />
                    <TrellisLink
                      to={getDashboardTo({
                        type: "advertisement",
                        id: ad_id,
                      })}
                      target={"_blank"}
                      className={css({
                        paddingRight: "medium",
                        textDecoration: "none",
                        fontSize: "medium",
                        fontWeight: 500,
                      })}
                    >
                      <small>{adName}</small>
                    </TrellisLink>
                  </>
                )}
              </div>
            );
          },
        }),
      );
    }

    if (dimensions.includes("match_type")) {
      cols.push(
        columnHelper.accessor("match_type", {
          id: "match_type",
          header: t(($) => $.str.columns["Match Type"]),
          cell: ({ getValue }) => {
            const raw = getValue();
            const selector = MATCH_TYPE_SELECTORS[raw];
            if (selector) return <span>{t(selector)}</span>;
            const fallback = raw
              .replaceAll(/([a-z])([A-Z])/g, "$1 $2")
              .toLowerCase()
              .replaceAll("_", " ");
            return <span>{fallback}</span>;
          },
          size: 100,
          meta: {
            columnFilterOptions: { format: "select" },
            columnSelectorOptions: {
              grouping: "configuration",
              description: t(($) => $.str.columnDescriptions["Target match type"]),
            },
          },
        }),
      );
    }

    if (dimensions.includes("campaign_id")) {
      cols.push(
        columnHelper.accessor("campaign_name", {
          id: "campaign_name",
          header: t(($) => $.str.columns["Campaign"]),
          size: 100,
          cell: ({ getValue }) => {
            return (
              <div className={css({ paddingLeft: "small" })}>
                <KeywordCell value={getValue()} columnWidth={100} />
              </div>
            );
          },
        }),
        columnHelper.accessor("ad_group_name", {
          id: "ad_group_name",
          header: t(($) => $.str.columns["Ad Group"]),
          size: 100,
          cell: ({ getValue }) => {
            return (
              <div className={css({ paddingLeft: "small" })}>
                <KeywordCell value={getValue()} columnWidth={100} />
              </div>
            );
          },
        }),
      );
    }

    cols.push(
      // Invisible columns to support filtering
      columnHelper.accessor("ad_id", {
        id: "ad_id",
        header: t(($) => $.str.columns["Media Plan"]),
        enableHiding: false,
        meta: {
          columnFilterOptions: {
            format: "media-plan",
          },
        },
      }),
      columnHelper.accessor("category_id", {
        id: "category_id",
        header: t(($) => $.str.columns["Category"]),
        enableHiding: false,
        meta: {
          columnFilterOptions: {
            format: "select",
          },
        },
      }),
    );

    cols.push(...standardMetricIds.map((metricId) => columnHelper.metric(metricId)));

    return cols;
  }, [dimensions, standardMetricIds, t]);

  return columns;
};

export default useSearchTermReportColumns;
